import { produce } from "immer";
import { TokenValidity, ValidToken } from "../@types/TokenValidity";
import { PasswordRequirements } from "../@types/PasswordRequirements";
import { CreateAccountFields } from "../@types/CreateAccountFields";
import { ParseKeys as IntlKey } from "i18next";
import { ErrorCodes } from "../@types/ErrorCodes";

type CreateAccountState = Record<
  keyof CreateAccountFields,
  { value: string; error?: IntlKey }
>;
export interface State {
  tokenValid: TokenValidity;
  passwordRequirements: PasswordRequirements;
  createAccount: CreateAccountState;
  submitting: boolean;
  error?: IntlKey;
  hasReadTnC: boolean;
  passwordRequirementsFetched: boolean;
}

export type ValidTokenState = State & { tokenValid: ValidToken };

export const initialState: State = {
  tokenValid: { status: "loading" },
  passwordRequirements: {
    minLength: 12,
    maxLength: 256,
    requireNumber: true,
    requireNonAlpha: true,
    requireMixedCase: true,
  },
  createAccount: {
    firstName: { value: "" },
    lastName: { value: "" },
    email: { value: "" },
    password: { value: "" },
    passwordConfirmation: { value: "" },
  },
  submitting: false,
  hasReadTnC: false,
  passwordRequirementsFetched: false,
};

export type Message =
  | { action: "complete-registration" }
  | { action: "notify-wrong-email" }
  | { action: "password-compromised" }
  | { action: "session-expired" }
  | { action: "session-missing" }
  | { action: "set-email"; value: string }
  | { action: "set-first-name"; value: string }
  | { action: "set-last-name"; value: string }
  | { action: "set-password"; value: string }
  | { action: "set-password-confirmation"; value: string }
  | { action: "set-password-requirements"; value: PasswordRequirements }
  | { action: "set-token-validity"; value: TokenValidity }
  | { action: "undefined-error" }
  | { action: "unmet-password-requirements" }
  | { action: "username-used" }
  | { action: "invalid-email-format" }
  | { action: "wrong-email-exceeded-allowed" }
  | { action: "invitation-expired" }
  | { action: "link-not-valid" }
  | { action: "read-terms-and-conditions" }
  | { action: "resend-invitation" };

export function reducer(state: State, message: Message): State {
  switch (message.action) {
    case "complete-registration":
      return produce(state, (next) => {
        next.submitting = true;
        Object.values(next.createAccount).forEach((e) => (e.error = undefined));
        next.error = undefined;
      });
    case "notify-wrong-email":
      return produce(state, (next) => {
        next.error = "errors.E0003";
        next.createAccount.email.error = "errors.E0003";
        next.submitting = false;
      });
    case "password-compromised":
      return produce(state, (next) => {
        next.error = "errors.E0005";
        next.createAccount.password.error = "errors.E0005";
        next.submitting = false;
      });
    case "set-token-validity":
      return setTokenValidity(state, message.value);
    case "set-password-requirements":
      return produce(state, (next) => {
        next.passwordRequirements = message.value;
        next.passwordRequirementsFetched = true;
      });
    case "set-first-name":
      return produce(state, (next) => {
        next.createAccount.firstName.value = message.value;
      });
    case "set-last-name":
      return produce(state, (next) => {
        next.createAccount.lastName.value = message.value;
      });
    case "set-email":
      return produce(state, (next) => {
        next.createAccount.email.value = message.value;
      });
    case "set-password":
      return produce(state, (next) => {
        next.createAccount.password.value = message.value;
      });
    case "set-password-confirmation":
      return produce(state, (next) => {
        next.createAccount.passwordConfirmation.value = message.value;
      });
    case "undefined-error":
      return produce(state, (next) => {
        next.error = "errors.E0002";
        next.submitting = false;
      });
    case "unmet-password-requirements":
      return produce(state, (next) => {
        next.error = "errors.E0001";
        next.createAccount.password.error = "errors.E0001";
        next.submitting = false;
      });
    case "username-used":
      return produce(state, (next) => {
        next.createAccount.email.error = "errors.E0006";
        next.error = "errors.E0006";
        next.submitting = false;
      });
    case "invalid-email-format":
      return produce(state, (next) => {
        next.createAccount.email.error = "errors.E0007";
        next.error = "errors.E0007";
        next.submitting = false;
      });
    case "wrong-email-exceeded-allowed":
      return produce(state, (next) => {
        next.error = "errors.E0004";
        next.submitting = false;
        next.tokenValid = {
          status: "error",
          code: ErrorCodes.E0101,
        };
      });
    case "link-not-valid":
      return produce(state, (next) => {
        next.tokenValid = {
          status: "error",
          code: ErrorCodes.E0101,
        };
      });
    case "invitation-expired":
      return produce(state, (next) => {
        next.tokenValid = {
          status: "error",
          code: ErrorCodes.E0102,
        };
      });
    case "read-terms-and-conditions":
      return produce(state, (next) => {
        next.hasReadTnC = true;
      });
    case "resend-invitation":
      return produce(state, (next) => {
        next.submitting = true;
      });
    default:
      return state; // if we got here, don't make any changes.
  }
}

function setTokenValidity(state: State, value: TokenValidity): State {
  return produce(state, (next) => {
    next.tokenValid = value;
    if (value.status === "ok") {
      next.createAccount.firstName.value = value.data.firstName;
      next.createAccount.lastName.value = value.data.lastName;
    }
  });
}
